import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ListRegionsQuery } from '../../../../../../graphql/fragment/audiences/ListRegions.generated';
import { exportType } from '../../../../../../modules/audiences/AudienceForm/constants';
import { AudienceFields, type AudienceForm, type Group } from '../../../../../../modules/audiences/AudienceForm/types';
import { isUSMarketGroup } from '../../../utils/marketOperatorRestrictions';
import { segmentsToRangeGroups } from '../../AudienceCriteria/components/expandableSections/demographics/demographicsUtils';

type SegmentsProps = {
  group: Group;
  listType: 'includes' | 'excludes';
  sx?: SxProps<Theme>;
  regions?: ListRegionsQuery['region'];
};

export const Segments = ({ group, sx, listType, regions = [] }: SegmentsProps) => {
  const { control } = useFormContext<AudienceForm>();
  const { t } = useTranslation();

  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });

  const regionCode = group.region ?? null;
  const regionName = regionCode ? (regions.find(region => region.code === regionCode)?.name ?? null) : null;
  const countriesCount = group.countries?.length ?? 0;
  const showRegionCriteria = !!regionName;

  // Legacy audiences have no region; isUSMarketGroup treats them as US.
  const showExtendedSections = observableExportType === exportType.OA_MATCH && isUSMarketGroup(group);

  const highlightColor = listType === 'excludes' ? 'error.main' : undefined;

  const directCount = group.direct?.segments?.length ?? 0;
  const predefinedCount = group.predefined?.segments?.length ?? 0;
  const segmentsTotal = directCount + predefinedCount;

  const lookalikeCount = group.lookalike?.segments?.length ?? 0;

  const genderCount = group.demographic_gender?.segments?.length ?? 0;
  const ageSegments = segmentsToRangeGroups(group.demographic_age?.segments ?? []).length;
  const demographicTotal = genderCount + ageSegments;

  return (
    <Box sx={sx}>
      {showRegionCriteria && (
        <Typography
          variant="body2Bold"
          sx={{
            mb: 0.5,
          }}
        >
          {regionName}
          {countriesCount > 0 && (
            <Box
              component="span"
              sx={{
                fontWeight: 'normal',
              }}
            >
              {' · '}
              {t('Countries')} [ {countriesCount} ]
            </Box>
          )}
        </Typography>
      )}

      <Typography variant="body2">
        {t('Segments')} [{' '}
        <Box
          component="span"
          sx={{
            color: highlightColor,
            display: 'inline',
          }}
        >
          {segmentsTotal || 'Any'}
        </Box>{' '}
        ]
      </Typography>

      {showExtendedSections && (
        <>
          <Typography variant="body2">
            {t('Lookalike Segments')} [{' '}
            <Box
              component="span"
              sx={{
                color: highlightColor,
                display: 'inline',
              }}
            >
              {lookalikeCount || 'Any'}
            </Box>{' '}
            ]
          </Typography>

          <Typography variant="body2">
            {t('Demographic')} [{' '}
            <Box
              component="span"
              sx={{
                color: highlightColor,
                display: 'inline',
              }}
            >
              {demographicTotal || 'Any'}
            </Box>{' '}
            ]
          </Typography>

          <Typography variant="body2">
            {t('User Location')} [{' '}
            <Box
              component="span"
              sx={{
                color: highlightColor,
                display: 'inline',
              }}
            >
              {group.location?.segments?.length || 'Any'}
            </Box>{' '}
            ]
          </Typography>
        </>
      )}
    </Box>
  );
};
